import { Col, Row, Spinner2, Text } from 'components';
import { theme } from 'lib';
import styled from 'styled-components';
import { ACTION_BADGE, LEVEL_BADGE, LEVEL_LABELS, SEVERITY_LABEL, severityColor } from '../constants';

/* ─────────────────────────── Layout ──────────────────────────────────────── */

/** Titled white card, used to group settings and lists. */
export const Section = ({ title, description, children, right, marg }) => (
  <Col
    noFlex
    wid="100%"
    pad="20px 22px"
    marg={marg}
    style={{
      backgroundColor: '#fff',
      borderRadius: '14px',
      border: `1px solid ${theme.GREY_20}`,
      boxShadow: '0 1px 2px rgba(20, 28, 40, 0.04)'
    }}
  >
    <Row center between noFlex wid="100%" marg={description ? '0 0 2px' : '0 0 14px'}>
      <Text h4 bold marg="0">
        {title}
      </Text>
      {right}
    </Row>
    {description && (
      <Text c1 col={theme.GREY_60} marg="0 0 16px" style={{ fontSize: '12.5px', lineHeight: 1.45 }}>
        {description}
      </Text>
    )}
    {children}
  </Col>
);

/* ─────────────────────────── Toggle ──────────────────────────────────────── */

export const Toggle = ({ on, onToggle, disabled }) => (
  <div
    onClick={disabled ? undefined : onToggle}
    style={{
      position: 'relative',
      width: '44px',
      height: '24px',
      borderRadius: '12px',
      backgroundColor: on ? theme.SECONDARY : theme.GREY_30,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'background-color 0.2s',
      flexShrink: 0
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: '3px',
        left: on ? '23px' : '3px',
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
      }}
    />
  </div>
);

/** Title + description on the left, a Toggle on the right. */
export const ToggleHeader = ({ title, description, on, onToggle, disabled }) => (
  <Row center between noFlex wid="100%">
    <Col noFlex style={{ minWidth: 0, marginRight: '16px' }}>
      <Text b2 bold marg="0" style={{ fontSize: '13.5px' }}>
        {title}
      </Text>
      {description && (
        <Text c1 col={theme.GREY_60} marg="2px 0 0" style={{ fontSize: '11.5px', lineHeight: 1.4 }}>
          {description}
        </Text>
      )}
    </Col>
    <Toggle on={on} onToggle={onToggle} disabled={disabled} />
  </Row>
);

/* ─────────────────────────── Badges ──────────────────────────────────────── */

const BadgePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 11px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  font-family: CeraPro;
  background: ${(props) => props.bg};
  color: ${(props) => props.col};
  border: 1px solid ${(props) => props.border};
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => props.col};
`;

/** Colour-coded action badge (Block=red, Flag=amber, Warn=yellow, Allow=grey). */
export const ActionBadge = ({ action }) => {
  const s = ACTION_BADGE[action] || ACTION_BADGE.Allow;
  return (
    <BadgePill bg={s.bg} col={s.col} border={s.border}>
      <Dot col={s.dot} />
      {action || '—'}
    </BadgePill>
  );
};

/** Trigger badge for word-list hits, shaped like CategorySeverityBadge: "Profanity · severe". */
export const ProfanityTriggerBadge = ({ level }) => {
  const s = LEVEL_BADGE[level];
  if (!s) return null;
  return (
    <BadgePill bg="#fff" col={s.col} border={theme.GREY_20}>
      <Dot col={s.dot} />
      Profanity
      <span style={{ color: theme.GREY_50, fontWeight: 600 }}>· {level}</span>
    </BadgePill>
  );
};

/** Word-list level badge (mild=yellow, moderate=orange, severe=red). */
export const LevelBadge = ({ level }) => {
  const s = LEVEL_BADGE[level];
  if (!s) return <Text c1 col={theme.GREY_50}>—</Text>;
  return (
    <BadgePill bg={s.bg} col={s.col} border={s.border}>
      <Dot col={s.dot} />
      {LEVEL_LABELS[level] || level}
    </BadgePill>
  );
};

/** Marker for texts blocked automatically by a severe word-list hit. */
export const AutoBlockedBadge = () => (
  <BadgePill bg="#FFF1F2" col="#E11D48" border="#FECDD3">
    <Dot col="#EF4444" />
    Auto-blocked · Profanity
  </BadgePill>
);

/** Category + severity pill, coloured by severity. */
export const CategorySeverityBadge = ({ category, severity }) => {
  if (!category) return <Text c1 col={theme.GREY_50}>—</Text>;
  const col = severityColor(severity);
  return (
    <BadgePill bg="#fff" col={col} border={theme.GREY_20}>
      <Dot col={col} />
      {category}
      {severity != null && (
        <span style={{ color: theme.GREY_50, fontWeight: 600 }}>
          · {(SEVERITY_LABEL[severity] || String(severity)).toLowerCase()}
        </span>
      )}
    </BadgePill>
  );
};

/* ─────────────────────────── States ──────────────────────────────────────── */

export const LoadingBlock = ({ height = '200px' }) => (
  <Row centerAll noFlex wid="100%" ht={height}>
    <Spinner2 />
  </Row>
);

export const EmptyState = ({ title = 'Nothing here', subtitle }) => (
  <Col
    centerAll
    noFlex
    wid="100%"
    pad="48px 0"
    gap="6px"
    style={{
      borderRadius: '14px',
      border: `1px dashed ${theme.GREY_30}`,
      background: theme.GREY_10
    }}
  >
    <Text h4 col={theme.GREY_70} marg="0">
      {title}
    </Text>
    {subtitle && (
      <Text c1 col={theme.GREY_60} marg="0" center style={{ fontSize: '12.5px' }}>
        {subtitle}
      </Text>
    )}
  </Col>
);

export const ErrorState = ({ message, onRetry }) => (
  <Col
    centerAll
    noFlex
    wid="100%"
    pad="40px 0"
    gap="10px"
    style={{
      borderRadius: '14px',
      border: `1px solid ${theme.SECONDARY_20}`,
      background: theme.SECONDARY_10
    }}
  >
    <Text b2 bold col={theme.SECONDARY} marg="0">
      {message || 'Something went wrong.'}
    </Text>
    {onRetry && (
      <button
        onClick={onRetry}
        style={{
          height: '32px',
          padding: '0 16px',
          borderRadius: '8px',
          border: `1px solid ${theme.SECONDARY}`,
          background: '#fff',
          color: theme.SECONDARY,
          fontFamily: 'CeraPro',
          fontWeight: 600,
          fontSize: '13px',
          cursor: 'pointer'
        }}
      >
        Try again
      </button>
    )}
  </Col>
);

/** Full-section access-denied state for a 403. */
export const AccessDenied = () => (
  <Col centerAll noFlex wid="100%" pad="80px 0" gap="10px">
    <div
      style={{
        width: '56px',
        height: '56px',
        borderRadius: '16px',
        background: theme.GREY_10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '6px'
      }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={theme.GREY_40} strokeWidth="1.6">
        <rect x="4" y="10" width="16" height="10" rx="2" />
        <path d="M8 10V7a4 4 0 018 0v3" />
      </svg>
    </div>
    <Text h4 col={theme.GREY_80} marg="0">
      Access denied
    </Text>
    <Text c1 col={theme.GREY_60} marg="0" center style={{ fontSize: '13px', maxWidth: '360px' }}>
      You don't have permission to view content moderation. This area is limited to P4P
      administrators.
    </Text>
  </Col>
);
